import type { StageDef } from './data/maps';
import type { MapEntity } from './types/MapEntity.type';
import { shuffle } from './utils/utils';

export const MIN_BASKET_COUNT = 2;
export const MAX_BASKET_COUNT = 12;
export const DEFAULT_BASKET_COUNT = 4;

/** 정원이 찬 바구니를 덮는 경사로. 열린 바구니가 바뀔 때마다 통째로 지우고 다시 만든다 */
export const BASKET_ROOF_GROUP = 'basket-roof';

// ── 맵 좌표 ─────────────────────────────────────────────────────────────────
// 미니맵이 26유닛 폭을 가정하므로(minimap.ts MINIMAP_UNITS) 그 안에 들어가야 한다
const WALL_LEFT = 0.75;
const WALL_RIGHT = 25.25;
/** 구슬은 10개씩 위로 쌓이며 생성된다. 기존 맵들과 같이 넉넉히 위까지 벽을 세운다 */
const WALL_TOP = -300;
// Marble 은 x 10.25~15.65 에 생성된다. 초기 스택이 끼지 않도록 그보다 넓게
const SPAWN_WALL_LEFT = 9.25;
const SPAWN_WALL_RIGHT = 16.5;
const FUNNEL_Y = 8;
/**
 * 슈트 반폭. 라운드 길이를 지배하는 값이라 바꿀 때는 `yarn simulate basketstress` 와
 * `basketfair` 를 함께 돌려야 한다.
 *
 * 좁히면 구슬이 한 줄로 줄을 서서 처리량이 떨어진다. 폭 1.2 에서는 초당 2~3개만 통과해
 * 100개 라운드가 평균 62초였다. 반대로 넓히면 구슬이 나란히 떨어져 생성 레인이 도착 칸으로
 * 이어지는 편향이 커진다. 폭 2.0 이 두 지표가 가장 좋은 지점이었다 (100개 평균 16초,
 * 생성순서별 최대 편차는 폭 1.2 보다도 낮음)
 */
const CHUTE_HALF_WIDTH = 1;
const CHUTE_CENTER = 13;
const CHUTE_LEFT = CHUTE_CENTER - CHUTE_HALF_WIDTH;
const CHUTE_RIGHT = CHUTE_CENTER + CHUTE_HALF_WIDTH;
const CHUTE_TOP = 12;
const CHUTE_BOTTOM = 14;
const SPLITTER_Y = 16.5;
const FLARE_MID_Y = 22;
const FLARE_END_Y = 28;

/** 바구니 입구(= 칸막이 윗변). 이 아래로 내려온 구슬은 그 칸에 담긴다 */
export const MOUTH_Y = 56;
export const FLOOR_Y = 62;
/** 바구니 바닥보다 아래. 여기까지 내려온 구슬은 어딘가로 새어나간 것이므로 구제한다 */
export const BASKET_GOAL_Y = 66;

/** 입구를 이만큼 지나야 담긴 것으로 본다. 칸막이 위에서 애매하게 판정되지 않을 깊이 */
const CAPTURE_DEPTH = 0.5;

const PADDLE_SPEED = 4;
const PADDLE_HALF_LENGTH = 1.5;
/**
 * 회전 날개 끝과 벽 사이에 반드시 남겨둘 틈. 날개는 kinematic 이라 사실상 무한 질량이므로,
 * 날개가 벽까지 쓸면 구석에 몰린 구슬을 영원히 위로 쳐올려 라운드가 끝나지 않는다.
 * 구슬 지름(0.5)보다 넉넉히 커서 항상 빠져나갈 길이 있어야 한다
 */
const PADDLE_WALL_GAP = 1;

// 경사로 형상
/** 낮은 끝을 옆 바구니 입구 안쪽까지 내밀어, 굴러떨어진 구슬이 칸막이 위에 얹히지 않게 한다 */
const ROOF_OVERHANG = 0.6;
/** 낮은 끝을 칸막이 윗변보다 살짝 위에 둔다. 딱 붙이면 꼭짓점에 구슬이 낀다 */
const ROOF_LIP = 0.15;
const ROOF_SLOPE = 0.14;
const ROOF_MIN_RISE = 0.8;
const ROOF_MAX_RISE = 3;

function polyline(points: [number, number][], group?: string): MapEntity {
  return {
    type: 'static',
    position: { x: 0, y: 0 },
    shape: { type: 'polyline', rotation: 0, points },
    props: { density: 1, angularVelocity: 0, restitution: 0 },
    ...(group ? { group } : {}),
  };
}

function paddle(x: number, y: number, angularVelocity: number): MapEntity {
  return {
    type: 'kinematic',
    position: { x, y },
    shape: { type: 'box', width: PADDLE_HALF_LENGTH, height: 0.1, rotation: 0 },
    props: { density: 1, angularVelocity, restitution: 0 },
    // 시작 위상을 라운드마다 무작위로 준다. 모든 날개가 같은 각도에서 출발하면 구슬이 나란히
    // 떨어질 때 같은 위상으로 맞아 생성 위치가 그대로 도착 칸으로 이어진다(측정으로 확인)
    angle: Math.random() * Math.PI * 2,
  };
}

function peg(x: number, y: number): MapEntity {
  return {
    type: 'static',
    position: { x, y },
    shape: { type: 'box', width: 0.3, height: 0.3, rotation: Math.PI / 4 },
    props: { density: 1, angularVelocity: 0, restitution: 0 },
  };
}

/** 벽 사이를 균등하게 채우는 회전 날개 한 줄. 인접한 날개는 서로 반대로 돌린다 */
function paddleRow(y: number, count: number, flip: boolean): MapEntity[] {
  const first = WALL_LEFT + PADDLE_HALF_LENGTH + PADDLE_WALL_GAP;
  const last = WALL_RIGHT - PADDLE_HALF_LENGTH - PADDLE_WALL_GAP;
  const step = count > 1 ? (last - first) / (count - 1) : 0;
  return Array.from({ length: count }, (_, i) =>
    paddle(first + step * i, y, (i % 2 === 0) === flip ? PADDLE_SPEED : -PADDLE_SPEED)
  );
}

function pegRow(y: number, spacing: number, offset: number): MapEntity[] {
  const pegs: MapEntity[] = [];
  for (let x = WALL_LEFT + offset; x < WALL_RIGHT; x += spacing) {
    pegs.push(peg(x, y));
  }
  return pegs;
}

/** 생성 구역 → 슈트 → 확산 구역 → 바구니 바닥까지 이어지는 외벽 한 덩어리 */
function containerWalls(): MapEntity {
  return polyline([
    [SPAWN_WALL_LEFT, WALL_TOP],
    [SPAWN_WALL_LEFT, FUNNEL_Y],
    [CHUTE_LEFT, CHUTE_TOP],
    [CHUTE_LEFT, CHUTE_BOTTOM],
    [(WALL_LEFT + CHUTE_LEFT) / 2, FLARE_MID_Y],
    [WALL_LEFT, FLARE_END_Y],
    [WALL_LEFT, FLOOR_Y],
    [WALL_RIGHT, FLOOR_Y],
    [WALL_RIGHT, FLARE_END_Y],
    [(WALL_RIGHT + CHUTE_RIGHT) / 2, FLARE_MID_Y],
    [CHUTE_RIGHT, CHUTE_BOTTOM],
    [CHUTE_RIGHT, CHUTE_TOP],
    [SPAWN_WALL_RIGHT, FUNNEL_Y],
    [SPAWN_WALL_RIGHT, WALL_TOP],
  ]);
}

export function basketBounds(count: number): { left: number; right: number }[] {
  const width = (WALL_RIGHT - WALL_LEFT) / count;
  return Array.from({ length: count }, (_, i) => ({
    left: WALL_LEFT + width * i,
    right: WALL_LEFT + width * (i + 1),
  }));
}

/**
 * 정원을 최대한 고르게 나눈다. 22개를 4칸이면 6/6/5/5.
 * 나머지를 받을 칸은 매 라운드 무작위로 고른다. 고정하면 늘 앞쪽 칸이 한 명 많아진다
 */
export function computeQuotas(total: number, count: number): number[] {
  const base = Math.floor(total / count);
  const quotas = new Array<number>(count).fill(base);
  const extra = total % count;
  for (const index of shuffle(Array.from({ length: count }, (_, i) => i)).slice(0, extra)) {
    quotas[index] += 1;
  }
  return quotas;
}

export function createBasketStage(count: number): StageDef {
  const entities: MapEntity[] = [containerWalls()];

  // 슈트 가운데 줄기만 좌우로 갈라주는 분배기. 슈트를 다 덮으면 생성 레인이 그대로
  // 좌/우로 확정되므로 폭의 절반만 덮는다
  const splitterHalf = CHUTE_HALF_WIDTH * 0.55;
  entities.push(
    polyline([
      [CHUTE_CENTER - splitterHalf, SPLITTER_Y + splitterHalf * 0.9],
      [CHUTE_CENTER, SPLITTER_Y],
      [CHUTE_CENTER + splitterHalf, SPLITTER_Y + splitterHalf * 0.9],
    ])
  );

  // 확산 구역. 회전 날개로 좌우로 흩고 못으로 줄기를 끊는다. 단이 얕으면 생성 위치가 그대로
  // 도착 칸으로 이어지므로(측정으로 확인) 날개 줄을 넉넉히 겹쳐 섞는다
  entities.push(...paddleRow(31, 5, true));
  entities.push(...pegRow(35.5, 2.2, 1.4));
  entities.push(...paddleRow(39, 4, false));
  entities.push(...pegRow(43.5, 2.2, 2.5));
  // 마지막 줄은 입구에서 충분히 떨어뜨린다. 경사로 꼭대기(입구 - 3)와 겹치면 구슬이 낀다
  entities.push(...paddleRow(47, 5, true));

  // 칸막이. 바깥 두 벽은 containerWalls 가 이미 세운다
  for (const bound of basketBounds(count).slice(1)) {
    entities.push(
      polyline([
        [bound.left, MOUTH_Y],
        [bound.left, FLOOR_Y],
      ])
    );
  }

  return {
    title: 'Teams',
    // zoomY 를 도달 불가능한 goalY 와 같이 두면 골인 줌인과 슬로모션이 모두 꺼진다.
    // 바구니 모드는 어느 칸에 들어가는지가 전부라 확대보다 전체 폭이 중요하다
    goalY: BASKET_GOAL_Y,
    zoomY: BASKET_GOAL_Y,
    // 좁은 창에서도 모든 칸이 한눈에 보이게 맵 전체 폭을 화면에 맞춘다
    viewWidth: WALL_RIGHT - WALL_LEFT + 1,
    entities,
  };
}

function riseFor(length: number): number {
  return Math.min(ROOF_MAX_RISE, Math.max(ROOF_MIN_RISE, length * ROOF_SLOPE));
}

export type BasketMember = { name: string; hue: number };

export type BasketState = {
  index: number;
  name: string;
  left: number;
  right: number;
  quota: number;
  members: BasketMember[];
};

export type BasketRenderState = {
  mouthY: number;
  floorY: number;
  total: number;
  captured: number;
  finished: boolean;
  baskets: BasketState[];
};

/** A, B, C… 어느 언어에서도 짧아서 좁은 바구니에도 들어간다 */
export function defaultBasketName(index: number): string {
  return String.fromCharCode(65 + (index % 26));
}

/**
 * 바구니 모드 한 라운드의 상태.
 *
 * 공평함은 물리가 아니라 정원으로 보장한다. 구슬이 어느 칸에 떨어지는지는 순수하게 물리가
 * 정하지만, 정원이 찬 칸은 경사로(뚜껑)로 덮어 더 들어가지 못하게 한다. 경사로는 가장 가까운
 * 열린 칸을 향해 기울어지므로 넘친 구슬이 그쪽으로 굴러간다
 */
export class BasketRun {
  readonly stage: StageDef;
  readonly count: number;

  private readonly _baskets: BasketState[];
  private _total = 0;
  private _captured = 0;
  private _finished = false;
  private _roofDirty = true;

  constructor(count: number, names: string[] = []) {
    this.count = Math.max(1, Math.floor(count));
    this.stage = createBasketStage(this.count);
    this._baskets = basketBounds(this.count).map((bound, index) => ({
      index,
      name: names[index]?.trim() || defaultBasketName(index),
      left: bound.left,
      right: bound.right,
      quota: 0,
      members: [],
    }));
  }

  get baskets(): readonly BasketState[] {
    return this._baskets;
  }

  get finished(): boolean {
    return this._finished;
  }

  get captured(): number {
    return this._captured;
  }

  /** 구슬 수가 확정된 뒤 정원을 나눈다 */
  setTotal(total: number): void {
    const quotas = computeQuotas(total, this.count);
    this._baskets.forEach((basket, index) => {
      basket.quota = quotas[index];
      basket.members = [];
    });
    this._total = total;
    this._captured = 0;
    this._finished = false;
    this._roofDirty = true;
  }

  finish(): void {
    this._finished = true;
  }

  isFull(index: number): boolean {
    const basket = this._baskets[index];
    return !basket || basket.members.length >= basket.quota;
  }

  /** 경사로를 다시 세워야 하는지. 확인하면서 플래그를 내린다 */
  consumeRoofDirty(): boolean {
    if (!this._roofDirty) return false;
    this._roofDirty = false;
    return true;
  }

  indexAt(x: number): number | null {
    const first = this._baskets[0];
    const last = this._baskets[this.count - 1];
    // 벽에 붙어 살짝 벗어난 정도는 양끝 칸으로 붙인다. 그보다 멀면 맵 밖으로 새어나간 것
    if (x < first.left - 0.5 || x > last.right + 0.5) return null;
    const width = first.right - first.left;
    return Math.min(this.count - 1, Math.max(0, Math.floor((x - first.left) / width)));
  }

  /** 입구 아래로 내려온 구슬을 담는다. 담았으면 그 바구니 index */
  capture(x: number, y: number, member: BasketMember): number | null {
    if (this._finished) return null;
    if (y < MOUTH_Y + CAPTURE_DEPTH) return null;
    const index = this.indexAt(x);
    if (index === null) return null;
    // 경사로를 세우는 한 프레임 사이에 정원을 넘겨 들어오는 구슬이 있을 수 있다. 정원 합계가
    // 전체 구슬 수와 같으므로, 남은 자리가 가장 많은 칸으로 보내도 최종 분배는 정확하다
    return this.isFull(index) ? this.putIntoEmptiest(member) : this._put(index, member);
  }

  /** 어느 칸에도 담기지 않고 맵 밖으로 빠진 구슬 구제용 */
  putIntoEmptiest(member: BasketMember): number | null {
    let target: BasketState | null = null;
    let mostRoom = 0;
    for (const basket of this._baskets) {
      const room = basket.quota - basket.members.length;
      if (room > mostRoom) {
        mostRoom = room;
        target = basket;
      }
    }
    return target ? this._put(target.index, member) : null;
  }

  private _put(index: number, member: BasketMember): number {
    const basket = this._baskets[index];
    basket.members.push(member);
    this._captured++;
    if (basket.members.length >= basket.quota) this._roofDirty = true;
    return index;
  }

  /**
   * 정원이 찬 칸들을 덮는 경사로. 연속으로 찬 구간마다 한 줄씩 만든다.
   * 한 줄로 이어 만들어야 이음매에 단이 생기지 않는다
   */
  buildRoof(): MapEntity[] {
    const full = this._baskets.map((basket) => this.isFull(basket.index));
    if (full.every((v) => v)) return []; // 다 찼으면 더 담을 곳이 없다

    const roof: MapEntity[] = [];
    let index = 0;
    while (index < this.count) {
      if (!full[index]) {
        index++;
        continue;
      }
      let end = index;
      while (end + 1 < this.count && full[end + 1]) end++;
      roof.push(this._runRamp(index, end));
      index = end + 1;
    }
    return roof;
  }

  /**
   * 연속으로 찬 구간 [a, b] 를 덮는 경사로.
   * 구간은 최대 구간이므로 a > 0 이면 a-1 은, b < count-1 이면 b+1 은 반드시 열려있다
   */
  private _runRamp(a: number, b: number): MapEntity {
    const lowY = MOUTH_Y - ROOF_LIP;
    const left = this._baskets[a].left;
    const right = this._baskets[b].right;
    const overhang = Math.min(ROOF_OVERHANG, (this._baskets[0].right - this._baskets[0].left) * 0.3);
    const exitLeft = left - overhang;
    const exitRight = right + overhang;

    // 한쪽이 벽이면 벽에서 시작해 열린 쪽으로 내려가는 외팔 경사로
    if (a === 0) {
      return polyline(
        [
          [left, lowY - riseFor(exitRight - left)],
          [exitRight, lowY],
        ],
        BASKET_ROOF_GROUP
      );
    }
    if (b === this.count - 1) {
      return polyline(
        [
          [right, lowY - riseFor(right - exitLeft)],
          [exitLeft, lowY],
        ],
        BASKET_ROOF_GROUP
      );
    }

    // 양옆이 다 열려있으면 가운데를 꼭대기로 하는 지붕. 양쪽 기울기를 한 폴리라인으로
    // 이어야 꼭대기에 단이 생기지 않는다
    const apexX = (left + right) / 2;
    const rise = riseFor(Math.max(apexX - exitLeft, exitRight - apexX));
    return polyline(
      [
        [exitLeft, lowY],
        [apexX, lowY - rise],
        [exitRight, lowY],
      ],
      BASKET_ROOF_GROUP
    );
  }

  get renderState(): BasketRenderState {
    return {
      mouthY: MOUTH_Y,
      floorY: FLOOR_Y,
      total: this._total,
      captured: this._captured,
      finished: this._finished,
      baskets: this._baskets,
    };
  }

  /** 클립보드 복사용 표 (헤더 포함) */
  toRows(): string[][] {
    const rows: string[][] = [['Team', 'Name']];
    for (const basket of this._baskets) {
      for (const member of basket.members) {
        rows.push([basket.name, member.name]);
      }
    }
    return rows;
  }
}
